import type { SettingsState } from 'src/components/settings';
import type {
  Theme,
  CSSObject,
  Components,
  ComponentsOverrides,
  ComponentsPropsList,
} from '@mui/material/styles';

import type { ThemeOptions } from '../types';

// ----------------------------------------------------------------------

type ComponentSlot<
  Name extends keyof ComponentsOverrides<Theme>,
  Slot extends keyof NonNullable<ComponentsOverrides<Theme>[Name]>,
> = NonNullable<ComponentsOverrides<Theme>[Name]>[Slot];

function getSlotStyles<
  Name extends keyof ComponentsOverrides<Theme>,
  Slot extends keyof NonNullable<ComponentsOverrides<Theme>[Name]>,
>(slot: ComponentSlot<Name, Slot>, props?: ComponentsPropsList[Name]): CSSObject {
  const slotStyles = typeof slot === 'function' && props ? slot(props) : (slot ?? {});

  return slotStyles;
}

// ----------------------------------------------------------------------

export function updateComponentsWithSettings(
  components?: Components<Theme>,
  settingsState?: SettingsState
): Pick<ThemeOptions, 'components'> {
  const MuiCard: Components<Theme>['MuiCard'] = {
    styleOverrides: {
      root: (props) => {
        const { theme } = props;

        const rootStyles = getSlotStyles<'MuiCard', 'root'>(
          components?.MuiCard?.styleOverrides?.root,
          props
        );

        return {
          ...rootStyles,
          ...(settingsState?.contrast === 'hight' && {
            boxShadow: theme.vars.customShadows.z1,
          }),
        };
      },
    },
  };

  const MuiCssBaseline: Components<Theme>['MuiCssBaseline'] = {
    styleOverrides: {
      html: {
        fontSize: settingsState?.fontSize,
      },
      /*
       * Override global do autofill para vencer o fundo azul do Chrome e
       * manter a paleta do tema.
       *
       * OBJETO com as vars de CSS, e seletor escrito como CSS: este
       * `styleOverrides` vai direto pro `<Global>` do Emotion, que não chama
       * função em valor aninhado (serializava `(props)=>...` como texto,
       * declaração inválida que o navegador descartava) nem converte
       * `WebkitAutofill` em `-webkit-autofill` quando a chave é um seletor.
       * Nas duas formas antigas a regra nunca chegou a valer.
       */
      'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active':
        {
          WebkitBoxShadow: '0 0 0 100px var(--palette-background-paper) inset !important',
          boxShadow: '0 0 0 100px var(--palette-background-paper) inset !important',
          WebkitTextFillColor: 'var(--palette-text-primary) !important',
          caretColor: 'var(--palette-text-primary)',
        },
    },
  };

  return {
    components: {
      MuiCard,
      MuiCssBaseline,
    },
  };
}
